import React, { createContext, Dispatch, SetStateAction, useContext, useState } from 'react';
import { WRITING_PROFORMA } from '../constants/navigation';
import { IPosition } from '../types';
import { DEFAULT_STATE } from '../utils/editTool';
import { ProformaContext } from './proformaContext';
import { TextPresentationPageContext } from './textPresentationPageContext';
import { UndoRedoContext } from './undoRedoContext';

type ContextDispatch = {
  highlighted: Record<string, boolean>;
  setHighlighted: Dispatch<Record<string, boolean>>;
  colorPickerPosition: IPosition;
  setColorPickerPosition: Dispatch<IPosition>;
  isColorsSectionVisible: boolean;
  setColorsSectionVisible: Dispatch<SetStateAction<boolean>>;
  isDrawingToolActive: boolean;
  setDrawingToolActive: Dispatch<SetStateAction<boolean>>;
  isLabelsSectionVisible: boolean;
  setLabelsSectionVisible: Dispatch<SetStateAction<boolean>>;

  isEditMode: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  isLeaveModalShown: boolean;
  handleLeavePage: (
    preventedAction: () => void,
    isGoingToChangeScreenMode: boolean,
    e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement> | BeforeUnloadEvent
  ) => boolean;
  closeLeaveModal: () => void;
  preventedAction: () => void;
  clearEditTool: (keepEditMode?: boolean) => void;
};

export const EditToolContext = createContext<ContextDispatch>({
  highlighted: DEFAULT_STATE,
  setHighlighted: () => null,
  colorPickerPosition: { x: 0, y: 0 },
  setColorPickerPosition: () => null,
  isColorsSectionVisible: false,
  setColorsSectionVisible: () => null,
  isDrawingToolActive: false,
  setDrawingToolActive: () => null,
  isLabelsSectionVisible: false,
  setLabelsSectionVisible: () => null,
  isEditMode: false,
  setEditMode: () => null,
  isLeaveModalShown: false,
  handleLeavePage: () => false,
  closeLeaveModal: () => null,
  preventedAction: () => null,
  clearEditTool: () => null,
});

export const EditToolContextProvider: React.FC = ({ children }): JSX.Element => {
  const [isEditMode, setEditMode] = useState(false);
  const { state } = useContext(TextPresentationPageContext);
  const { isEventsNotEmpty: isExemplarChanged, clearEvents } = useContext(UndoRedoContext);
  const { isChanged: isProformaChanged } = useContext(ProformaContext);
  const [colorPickerPosition, setColorPickerPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [highlighted, setHighlighted] = useState<Record<string, boolean>>({
    ...DEFAULT_STATE,
    notes: state.notes[0]?.isManuallyHidden,
  });
  const [isColorsSectionVisible, setColorsSectionVisible] = useState(false);
  const [isDrawingToolActive, setDrawingToolActive] = useState(false);
  const [isLabelsSectionVisible, setLabelsSectionVisible] = useState(false);
  const [isLeaveModalShown, setIsLeaveModalShown] = useState(false);
  const [preventedAction, setPreventedAction] = useState<() => void>(() => null);

  const closeLeaveModal = () => {
    setIsLeaveModalShown(false);
  };

  const handleLeavePage = (
    action: () => void,
    isGoingToChangeScreenMode: boolean,
    e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement> | BeforeUnloadEvent
  ): boolean => {
    const isProformaPage = window.location.pathname.includes(WRITING_PROFORMA);
    const isProformaChangedAndGoingToLeaveIWP = isProformaChanged && isProformaPage;

    if ((isExemplarChanged || isProformaChangedAndGoingToLeaveIWP) && !isGoingToChangeScreenMode) {
      e?.preventDefault();
      setIsLeaveModalShown(true);
      setPreventedAction(() => action);
      return true;
    }

    setEditMode(isEditMode);

    return false;
  };

  const clearEditTool = (keepEditMode = false) => {
    setEditMode(keepEditMode || false);
    clearEvents();
    setIsLeaveModalShown(false);
    setPreventedAction(() => null);
  };

  return (
    <EditToolContext.Provider
      value={{
        highlighted,
        setHighlighted,
        colorPickerPosition,
        setColorPickerPosition,
        isColorsSectionVisible,
        setColorsSectionVisible,
        isDrawingToolActive,
        setDrawingToolActive,
        isLabelsSectionVisible,
        setLabelsSectionVisible,
        isEditMode,
        setEditMode,
        isLeaveModalShown,
        handleLeavePage,
        closeLeaveModal,
        preventedAction,
        clearEditTool,
      }}
    >
      {children}
    </EditToolContext.Provider>
  );
};
